import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Textarea from '@amzn/meridian/textarea';
import React, { useCallback, useState } from 'react';
import { Logger } from '../../utils/logger';
import { Button, Text, TranslatedString, useTranslation } from '../blocks';
import { CountrySelect } from '../composites';

export interface RequestRegistrationDialogProps {
    label: TranslatedString;
    dataTestId?: string;
}

const returns = 'Returns';
const claims = 'Seller Claims';
const admin = 'Reseller Admin';
const internal = 'Internal Amazon Employees';

const defaultReturnsEmail = 'amazon-devices-returns@amazon.com';
const claimsEmail = 'reseller-portal-access@amazon.com';
const ringEmail = 'ring-reseller-portal-access@amazon.com';

const countryEmailMap: Partial<Record<string, string>> = {
    JP: 'amazon-jp-offline-returns@amazon.com',
};

const RequestRegistrationDialog = ({ label, dataTestId }: RequestRegistrationDialogProps) => {
    const { t } = useTranslation('requestRegistrationDialog');
    const [open, setOpen] = useState(false);
    const onClickButton = useCallback(() => {
        Logger.debug('Opening registration dialog.');
        setOpen(true);
    }, []);
    const onClose = useCallback(() => setOpen(false), []);
    const userTypes = [
        { label: t('requestRegistration-requestTypeReturns'), value: returns },
        { label: t('requestRegistration-requestTypeClaims'), value: claims },
        { label: t('requestRegistration-requestTypeAdmin'), value: admin },
        { label: t('requestRegistration-requestTypeAmazon'), value: internal },
    ];
    const [selectedUserType, setSelectedUserType] = useState([]);
    const accountTypes = [
        { label: 'Amazon Devices', value: 'Amazon Devices' },
        { label: 'Ring', value: 'Ring' },
        { label: 'Blink', value: 'Blink' },
        { label: 'Dropship', value: 'Dropship' },
    ];
    const [selectedAccountType, setSelectedAccountType] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('');

    const getReturnsEmail = (country: string) => {
        return countryEmailMap[country] ? `${countryEmailMap[country]}; ${defaultReturnsEmail}` : defaultReturnsEmail;
    };

    const getEmailAddress = (userType: string[], accountType: string[], country: string) => {
        if (userType.includes(returns)) {
            return getReturnsEmail(country);
        }
        if (userType.includes(claims)) {
            if (accountType.includes('Ring')) {
                return ringEmail;
            }
            return claimsEmail;
        }

        return getReturnsEmail(country);
    };

    const getEmailBody = (userType: string[], accountType: string[], country: string) => {
        let roles = '';
        if (userType.includes(returns)) {
            roles += t('requestRegistration-reseller-creator');
            roles += t('requestRegistration-reseller-viewer');
        }
        if (userType.includes(claims)) {
            roles += t('requestRegistration-claims-creator');
            roles += t('requestRegistration-claims-viewer');
        }
        if (userType.includes(admin)) {
            roles += t('requestRegistration-reseller-admin');
        }

        if (roles.length > 0) {
            roles = t('requestRegistration-requested-role') + roles;
        }

        return (
            t('requestRegistration-intro') +
            t('requestRegistration-fields') +
            roles +
            t('requestRegistration-outro') +
            getEmailAddress(userType, accountType, country)
        );
    };

    const getBodyContent = (userType: string[], accountType: string[], country: string) => {
        if (userType.includes('Internal Amazon Employees')) {
            return (
                <Textarea
                    value={t('requestRegistration-body-amazon-employees')}
                    label={t('requestRegistration-bodyLabel-amazon-employees')}
                    onChange={() => {}}
                />
            );
        }

        return (
            <Column spacing={'small'}>
                <Textarea
                    value={getEmailAddress(userType, accountType, country)}
                    label={t('requestRegistration-sendToEmailAddressLabel')}
                    rows={1}
                    onChange={() => {}}
                />
                <Textarea
                    value={`${t('requestRegistration-subject')}-${userType}-${accountType}`}
                    label={t('requestRegistration-subjectLabel')}
                    rows={1}
                    onChange={() => {}}
                />
                <Textarea
                    value={getEmailBody(userType, accountType, country)}
                    label={t('requestRegistration-bodyLabel')}
                    onChange={() => {}}
                />
            </Column>
        );
    };

    return (
        <Row widths={['fit', 'fill']}>
            <Button onClick={onClickButton} data-testid={`${dataTestId}.link`}>
                <Text color={'inverted'}>{label}</Text>
            </Button>
            <Modal open={open} onClose={onClose} title={label}>
                <Column spacing={'small'}>
                    <Text type={'h100'}>{t('requestRegistration-requestAccessSummary')}</Text>
                    <Table headerRows={0} showDividers={false} data-testid={'actionHelpTable'} spacing={'small'}>
                        <TableRow>
                            <TableCell width={'5%'}>
                                <Text type={'h100'}>{t.cognate('1')}</Text>
                            </TableCell>
                            <TableCell>
                                <Text>{t('requestRegistration-requestAccessStep1')}</Text>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell width={'5%'}>
                                <Text type={'h100'}>{t.cognate('2')}</Text>
                            </TableCell>
                            <TableCell>
                                <Text>{t('requestRegistration-requestAccessStep2')}</Text>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell width={'5%'}>
                                <Text type={'h100'}>{t.cognate('3')}</Text>
                            </TableCell>
                            <TableCell>
                                <Text>{t('requestRegistration-requestAccessStep3')}</Text>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell width={'5%'}>
                                <Text type={'h100'}>{t.cognate('4')}</Text>
                            </TableCell>
                            <TableCell>
                                <Text>{t('requestRegistration-requestAccessStep4')}</Text>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell width={'5%'}>
                                <Text type={'h100'}>{t.cognate('5')}</Text>
                            </TableCell>
                            <TableCell>
                                <Text>{t('requestRegistration-requestAccessStep5')}</Text>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell width={'5%'}>
                                <Text type={'h100'}>{t.cognate('6')}</Text>
                            </TableCell>
                            <TableCell>
                                <Text>{t('requestRegistration-requestAccessStep6')}</Text>
                            </TableCell>
                        </TableRow>
                    </Table>
                    <Divider size={'small'} spacingBefore={'small'} />
                    <Column width={'100%'} spacing={'small'} spacingInset={'none'}>
                        <Select
                            value={selectedUserType}
                            onChange={(e) => {
                                setSelectedUserType(e);
                            }}
                            width={'100%'}
                            placeholder={t('requestRegistration-requestTypePlaceholder')}
                            label={t('requestRegistration-requestTypeLabel')}
                        >
                            {userTypes.map((userType) => {
                                return (
                                    <SelectOption key={userType.value} value={userType.value} label={userType.label} />
                                );
                            })}
                        </Select>
                    </Column>
                    <Column width={'100%'} spacing={'small'} spacingInset={'none'}>
                        <Select
                            value={selectedAccountType}
                            onChange={(e) => {
                                setSelectedAccountType(e);
                            }}
                            width={'100%'}
                            placeholder={t('requestRegistration-accountTypePlaceholder')}
                            label={t('requestRegistration-accountTypeLabel')}
                        >
                            {accountTypes.map((accountType) => {
                                return (
                                    <SelectOption
                                        key={accountType.value}
                                        value={accountType.value}
                                        label={accountType.label}
                                    />
                                );
                            })}
                        </Select>
                    </Column>
                    <Column>
                        <CountrySelect
                            value={selectedCountry}
                            onChange={setSelectedCountry}
                            placeholder={t('requestRegistration-countryPlaceholder')}
                        />
                    </Column>
                    {selectedUserType &&
                        selectedUserType.length > 0 &&
                        selectedAccountType &&
                        selectedAccountType.length > 0 &&
                        selectedCountry &&
                        getBodyContent(selectedUserType, selectedAccountType, selectedCountry)}
                </Column>
            </Modal>
        </Row>
    );
};

export { RequestRegistrationDialog };
